/**
 * Export the screen — to the clipboard or a `.csv`, as symbols, symbols and a
 * price, or every column showing. Any screen, not only a list.
 * `lib/screener-export.ts` decides the text; this only asks.
 */

import { useEffect, useState } from "react";
import type { ScreenerExporter } from "@qufolio/screener-grid";

import {
  exportCount,
  exportFileName,
  exportText,
  type ExportShape,
  type ExportTarget,
} from "../lib/screener-export";
import type { TrackList } from "../lib/track-lists";

export interface ScreenerExportDialogProps {
  exporter: ScreenerExporter;
  /** What is on screen — the list's name, or the screen's. The title and the file name. */
  title: string;
  /** Set when the screen is one of the user's lists: its price is the *added at* figure. */
  list?: TrackList;
  onClose: () => void;
}

function download(text: string, name: string) {
  const url = URL.createObjectURL(new Blob([text], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function ScreenerExportDialog({ exporter, title, list, onClose }: ScreenerExportDialogProps) {
  const [target, setTarget] = useState<ExportTarget>("clipboard");
  const [shape, setShape] = useState<ExportShape>("symbol");
  const [status, setStatus] = useState<string | null>(null);
  const count = exportCount(exporter);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const run = async () => {
    const text = exportText(exporter, { shape, target, list });
    if (target === "csv") {
      download(text, exportFileName(title));
      onClose();
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      setStatus(`Copied ${count === 1 ? "1 symbol" : `${count} symbols`}.`);
    } catch (e) {
      setStatus(`The browser refused the clipboard: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const radio = <T extends string>(name: string, value: T, current: T, set: (v: T) => void, label: string) => (
    <label style={OPTION}>
      <input type="radio" name={name} checked={current === value} onChange={() => set(value)} />
      {label}
    </label>
  );

  return (
    <div style={SCRIM}>
      <div role="dialog" aria-label={`Export ${title}`} style={PANEL}>
        <h4 style={TITLE}>
          Export {title} <span style={{ color: "var(--qf-text-muted)", fontWeight: 400 }}>({count})</span>
        </h4>

        <div style={ROW}>
          <span style={LABEL}>To</span>
          <div style={GROUP}>
            {radio("target", "clipboard", target, setTarget, "Clipboard")}
            {radio("target", "csv", target, setTarget, "CSV file")}
          </div>
        </div>

        <div style={ROW}>
          <span style={LABEL}>Columns</span>
          <div style={{ ...GROUP, flexDirection: "column", alignItems: "flex-start" }}>
            {radio("shape", "symbol", shape, setShape, "Symbol only")}
            {radio("shape", "price", shape, setShape, list ? "Symbol + price added" : "Symbol + price")}
            {radio("shape", "all", shape, setShape, "All columns showing")}
          </div>
        </div>

        {status ? <p style={NOTE}>{status}</p> : null}

        <div style={FOOT}>
          <button style={BTN} onClick={onClose}>
            {status ? "Close" : "Cancel"}
          </button>
          <button style={{ ...BTN, ...PRIMARY }} onClick={() => void run()} disabled={count === 0}>
            Export
          </button>
        </div>
      </div>
    </div>
  );
}

const SCRIM: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 1100,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "rgba(0,0,0,.35)",
};

const PANEL: React.CSSProperties = {
  width: "min(340px, calc(100vw - 32px))",
  padding: "14px 16px 16px",
  background: "var(--qf-surface)",
  border: "1px solid var(--qf-border)",
  borderRadius: 5,
  boxShadow: "0 14px 40px -12px rgba(0,0,0,.6)",
  color: "var(--qf-text)",
  fontSize: 12,
};

const TITLE: React.CSSProperties = { margin: "0 0 13px", fontSize: 13, fontWeight: 600 };

const ROW: React.CSSProperties = { display: "flex", alignItems: "flex-start", gap: 9, margin: "0 0 10px" };

const LABEL: React.CSSProperties = { width: 58, flex: "none", color: "var(--qf-text-muted)", fontSize: 11, paddingTop: 2 };

const GROUP: React.CSSProperties = { display: "flex", gap: 12, flexWrap: "wrap" };

const OPTION: React.CSSProperties = { display: "flex", alignItems: "center", gap: 5, fontSize: 11.5, cursor: "pointer" };

const NOTE: React.CSSProperties = { margin: "4px 0 0", fontSize: 11, color: "var(--qf-text-muted)" };

const FOOT: React.CSSProperties = { display: "flex", justifyContent: "flex-end", gap: 7, marginTop: 14 };

const BTN: React.CSSProperties = {
  border: "1px solid var(--qf-border)",
  background: "var(--qf-background)",
  color: "var(--qf-text)",
  borderRadius: 3,
  padding: "4px 10px",
  font: "inherit",
  fontSize: 11.5,
  cursor: "pointer",
};

const PRIMARY: React.CSSProperties = {
  background: "var(--qf-accent)",
  borderColor: "var(--qf-accent)",
  color: "var(--qf-background)",
  fontWeight: 600,
};
